import styled from 'styled-components';

import { TAG_TONE_COLORS, tagToneColors } from 'components/constants/tagTones';
import type { TagTone } from 'components/constants/tagTones';

/**
 * Styled tokens shared between the «detail-page control centers»
 * (StudentDetail, GroupDetail). Originally lived in
 * `StudentDetail.styled.ts`; promoted here so both pages render their
 * HeaderBar, KpiStrip, inline popups, and section tables with one set
 * of tokens.
 *
 * Page-local compositions (e.g. `AgreementCardWrap`, `AgreementHeader`
 * for StudentDetail) stay in the page's own `.styled.ts` to keep
 * page-specific layout out of the shared surface.
 */

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
`;

/** Цвета кнопки по тону: `danger` — необратимое действие (отмена договора). */
const ACTION_TONES = {
  primary: { border: '#2563eb', bg: '#2563eb', fg: '#fff', hover: '#1d4ed8' },
  danger: { border: '#dc2626', bg: '#dc2626', fg: '#fff', hover: '#b91c1c' },
  ghost: { border: '#e2e8f0', bg: '#fff', fg: '#0f172a', hover: '#f8fafc' },
} as const;

export const PrimaryActionButton = styled.button<{
  tone?: 'primary' | 'ghost' | 'danger';
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ tone }) => ACTION_TONES[tone ?? 'ghost'].border};
  background: ${({ tone }) => ACTION_TONES[tone ?? 'ghost'].bg};
  color: ${({ tone }) => ACTION_TONES[tone ?? 'ghost'].fg};
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;

  &:hover:not(:disabled) {
    background: ${({ tone }) => ACTION_TONES[tone ?? 'ghost'].hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  margin: 8px 0 4px;
`;

export const ProgressFill = styled.div<{
  pct: number;
  tone: 'ok' | 'warn' | 'danger';
}>`
  height: 100%;
  width: ${({ pct }) => Math.max(0, Math.min(100, pct))}%;
  background: ${({ tone }) =>
    tone === 'danger' ? '#dc2626' : tone === 'warn' ? '#d97706' : '#16a34a'};
  transition: width 0.25s;
`;

export const Subblock = styled.div`
  margin-top: 16px;
`;

export const SubblockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
`;

export const SubblockHeaderCount = styled.span`
  color: #5b6775;
  font-weight: 600;
`;

export const SubblockRight = styled.div`
  margin-left: auto;
  display: flex;
  gap: 6px;
`;

export const SmallTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  thead th {
    text-align: left;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #5b6775;
    font-weight: 600;
    padding: 6px 8px;
    border-bottom: 1px solid #e2e8f0;
  }

  tbody td {
    padding: 8px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

export const OrphanRowTone = styled.tr`
  background: #fff7ed;
`;

export const PaymentLink = styled.a`
  color: #16a34a;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const Muted = styled.span`
  color: #5b6775;
`;

export const ChipLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: #2563eb;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// ТЗ 0096: палитра тонов переехала в `components/constants/tagTones`.
export { TAG_TONE_COLORS, tagToneColors };
export type { TagTone };

export const Tag = styled.span<{ tone?: TagTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  background: ${({ tone }) => tagToneColors(tone).bg};
  color: ${({ tone }) => tagToneColors(tone).fg};
`;

/* ============ Inline popup form bits ============ */

export const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
`;

export const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
`;

export const FormInput = styled.input`
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 14px;
  background: #fff;
  outline: none;
  font-weight: 500;
  color: #0f172a;

  &:focus {
    border-color: #2563eb;
  }
`;

/**
 * Многострочное поле формы (ТЗ 0040, доводка).
 *
 * 🔴 Не косметика. Поля, которые правились через `FormInput` высотой 36px, на
 * сервере объявлены `TextField` — то есть длина не ограничена вовсе: описание
 * профессии, «чем занимается», описание медали и навыка. Владелец открыл форму
 * профессии и увидел абзац, из которого в поле помещается треть строки:
 * «сделаем всё-таки многострочным в большом текстовом блоке, чтобы удобно было
 * и читать, и редактировать».
 *
 * `resize: vertical` — можно растянуть под конкретный текст, но не разъехаться
 * вширь и не сломать колонку формы.
 */
export const FormTextarea = styled.textarea`
  min-height: 96px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.45;
  background: #fff;
  outline: none;
  font-weight: 500;
  color: #0f172a;
  resize: vertical;

  &:focus {
    border-color: #2563eb;
  }

  &:disabled {
    background: #f8fafc;
    color: #64748b;
  }
`;

export const FormSelect = styled.select`
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 14px;
  background: #fff;
  outline: none;
  font-weight: 500;
  color: #0f172a;

  &:focus {
    border-color: #2563eb;
  }
`;

/* ============ Поля формы в выдвижной панели (ТЗ 0050) ============ */

/**
 * Одно поле формы: подпись сверху, поле, подсказка под ним.
 *
 * 🔴 Заведено потому, что формы раздела дофинансирования были собраны из
 * `S.FilterGroup` — примитива ФИЛЬТРОВОЙ ПАНЕЛИ (`flex; align-items:center`).
 * Там это верно: подпись и контрол стоят в строку над таблицей. В форме тот же
 * примитив ставит подпись, поле и подсказку СОСЕДЯМИ в одной строке — подпись
 * у каждой строки своей ширины (колонки не выстраиваются), а подсказка
 * сжимается в узкий рваный столбец сбоку. Владелец прислал два скриншота
 * ровно с этим.
 *
 * `FilterGroup` при этом не трогаем: у него десяток правомерных вызывающих на
 * других страницах (`shared-constant-audit-all-callers`).
 */
export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  /* Поля тянутся на всю ширину ячейки — иначе «min-width: 220px» у одних и
     авто-ширина у других дают лесенку из краёв. */
  & > input,
  & > select,
  & > textarea {
    width: 100%;
  }
`;

/** Подпись поля. Та же типографика, что у `FilterLabel`, — раздел один. */
export const FormFieldLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
`;

/**
 * Подсказка ПОД полем, на всю его ширину.
 *
 * Отдельно от `Muted`: та живёт где угодно и в строке, а эта обязана быть
 * блоком — иначе снова станет соседом поля и снова сожмётся.
 */
export const FormHint = styled.span`
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: #64748b;
`;

/** Ошибка ПОД полем (или под строкой таблицы): блоком, с переносом. */
export const FieldError = styled.span`
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: #b91c1c;
  white-space: normal;
`;

/** Только для экранного диктора: вежливые объявления «сохранено». */
export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Строка формы: несколько полей рядом, с переносом на узкой панели.
 *
 * `auto-fit` + `minmax`, а не фиксированные колонки: панель бывает 680 и 760
 * пикселей, и жёсткая сетка на узкой обрезала бы правое поле.
 */
export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px 16px;
  align-items: start;
`;

/** Вертикальный ритм формы: поля и строки одним зазором, без ручных марджинов. */
export const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/**
 * Сетка чекбоксов.
 *
 * 🔴 Чекбоксы стояли голыми `DS.FilterCheck` подряд — «inline-flex», между
 * которыми нет ничего, кроме переноса строки в разметке. На экране это
 * читалось как «берёт безработных☐принимает заявки от работодателя☑учитывает
 * инвалидность»: владелец назвал это «склеенные элементы друг с другом».
 */
export const CheckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 4px 16px;
`;

export const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

export const InlineNotice = styled.div`
  font-size: 12px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 8px 10px;
  line-height: 1.4;
`;
